/* eslint-disable indent */
import React from 'react';
import styleable from 'react-styleable';
import PropTypes from 'prop-types';
import { Button } from '@3plearning/react-components';
import IconMoveVertical from '@3plearning/3p-teacheradmin-assets/assets/icons/icon_move-vertical.svg';
import { translate } from '../../../../../helpers/locale-helpers';

import css from './progress-display.scss';

import { SQ_STANDARDS_COUNT_BOX_TYPES } from '../../../skill-quests-mastery-constants';
import { AccuracyGraph } from '../../../../../components/accuracy-graph/accuracy-graph';
import { getStudentFullname } from '../../../skill-quests-mastery-helpers';
import { AccuracyOverviewGraph } from '../../../../../components/accuracy-overview-graph/accuracy-overview-graph';
import { useIsGraphClickable } from './use-show-accuracy-graph';

const ProgressDisplay = ({
    skillQuestsMasteryStudentData,
    openModal,
    getDateLabel
}) => {
    const text = {
        standardsInProgressLabel: translate(
            'Progress Display - Standards in progress'
        ),
        standardsCompletedLabel: translate(
            'Progress Display - Standards completed'
        ),
        learningJourneysInProgressLabel: translate(
            'Progress Display - Skill Quests in progress'
        ),
        learningJourneysCompletedLabel: translate(
            'Progress Display - Skill Quests completed'
        ),
        averageAccuracyLabel: translate('Progress Display - Average overview'),
        highestAccuracyLabel: translate('Progress Display - Highest accuracy'),
        lowestAccuracyLabel: translate('Progress Display - Lowest accuracy'),
        accuracyOverviewLabel: translate(
            'Progress Display - Accuracy overview'
        ),
        dashLabel: translate('Progress Display - Dash Label'),
        percentageLabel: (accuracy) => `${accuracy}%`
    };

    const getProgressCompletedValue = (progressCount, availableCount) => {
        const { dashLabel } = text;

        if (availableCount !== 0) {
            return `${progressCount}/${availableCount}`;
        }

        return dashLabel;
    };

    const getAccuracyValue = (accuracy) => {
        const { percentageLabel, dashLabel } = text;

        if (accuracy) {
            return percentageLabel(accuracy);
        }

        return dashLabel;
    };

    const getContentBoxValues = () => {
        const {
            reportData: {
                learningJourneysInProgressCount,
                learningJourneysCompletedCount,
                learningJourneysAvailableCount,
                standardsInProgressCount,
                standardsCompletedCount,
                standardsAvailableCount,
                averageAccuracy,
                highestAccuracies,
                lowestAccuracies
            }
        } = skillQuestsMasteryStudentData;

        const highestAccuracyValue =
            highestAccuracies &&
            highestAccuracies[0].accuracies &&
            highestAccuracies[0].accuracies[0] &&
            highestAccuracies[0].accuracies[0].accuracy;
        const lowestAccuracyValue =
            lowestAccuracies &&
            lowestAccuracies[0].accuracies &&
            lowestAccuracies[0].accuracies[0] &&
            lowestAccuracies[0].accuracies[0].accuracy;

        return {
            standardsInProgressValue: getProgressCompletedValue(
                standardsInProgressCount,
                standardsAvailableCount
            ),
            standardsCompletedValue: getProgressCompletedValue(
                standardsCompletedCount,
                standardsAvailableCount
            ),
            learningJourneysInProgressValue: getProgressCompletedValue(
                learningJourneysInProgressCount,
                learningJourneysAvailableCount
            ),
            learningJourneysCompletedValue: getProgressCompletedValue(
                learningJourneysCompletedCount,
                learningJourneysAvailableCount
            ),
            averageAccuracyValue: getAccuracyValue(averageAccuracy),
            highestAccuracyValue: getAccuracyValue(highestAccuracyValue),
            lowestAccuracyValue: getAccuracyValue(lowestAccuracyValue)
        };
    };

    const getAccuracyGraphContent = (averageAccuracies = []) => {
        const { dashLabel } = text;

        if (averageAccuracies.length) {
            return <AccuracyGraph averageAccuracies={averageAccuracies} />;
        }

        return <div>{dashLabel}</div>;
    };

    const getAccuracyModalContent = (accuracyArray, title) => {
        const { reportData } = skillQuestsMasteryStudentData;

        openModal({
            title: null,
            content: (
                <div className={css['accuracy-modal']}>
                    <h2 className={css['accuracy-modal-header']}>
                        <span className={css['header-text']}>
                            {getStudentFullname(reportData)}
                        </span>
                        <span className={css['header-separator']} />
                        <span className={css['header-text']}>{title}</span>
                    </h2>
                    <div className={css['date-range']}>{getDateLabel()}</div>
                    <ul className={css['accuracy-list']}>
                        {accuracyArray.map((result) => {
                            const { accuracies, standardTopicName } = result;

                            return (
                                <div className={css['accuracy-item-container']}>
                                    <div className={css.topicName}>
                                        {standardTopicName}
                                    </div>
                                    {accuracies.map(
                                        ({ accuracy, learningJourneyName }) => (
                                            <li
                                                className={css['accuracy-item']}
                                                key={`${learningJourneyName}-${accuracy}`}
                                            >
                                                <p>{learningJourneyName}</p>
                                                <p>{accuracy}%</p>
                                            </li>
                                        )
                                    )}
                                </div>
                            );
                        })}
                    </ul>
                </div>
            ),
            buttons: []
        });
    };

    const getAccuracyGraphModalContent = (averageAccuraciesHistory) => {
        const { reportData } = skillQuestsMasteryStudentData;

        const { accuracyOverviewLabel } = text;

        openModal({
            title: null,
            content: (
                <div className={css['accuracy-modal']}>
                    <h2 className={css['accuracy-modal-header']}>
                        <span className={css['header-text']}>
                            {getStudentFullname(reportData)}
                        </span>
                        <span className={css['header-separator']} />
                        <span className={css['header-text']}>
                            {accuracyOverviewLabel}
                        </span>
                    </h2>
                    <div className={css['date-range']}>{getDateLabel()}</div>
                    <AccuracyOverviewGraph
                        dateRange={getDateLabel()}
                        averageAccuracies={averageAccuraciesHistory}
                    />
                </div>
            ),
            buttons: []
        });
    };

    const [showAccuracyOverviewGraph] = useIsGraphClickable(
        skillQuestsMasteryStudentData
    );

    const renderBox = (label, value, modalCallback = null) => {
        if (label && value) {
            const boxClassName = `${css.box} ${modalCallback &&
                css['box-button']}`;
            return (
                <button
                    className={boxClassName}
                    onClick={() => modalCallback()}
                >
                    <p className={css.label}>{label}</p>
                    <div className={css.value}>{value}</div>
                    {modalCallback && (
                        <div className={css['modal-button-container']}>
                            <Button
                                onClick={() => {}}
                                icon={IconMoveVertical}
                                className={css['modal-button']}
                            />
                        </div>
                    )}
                </button>
            );
        }

        return null;
    };

    const renderContentBox = (
        leftLabel,
        leftValue,
        leftModalCallback,
        rightLabel,
        rightValue,
        rightModalCallback,
        type
    ) => (
        <div className={`${css['content-box']} ${css[type]}`}>
            {renderBox(leftLabel, leftValue, leftModalCallback)}
            {renderBox(rightLabel, rightValue, rightModalCallback)}
        </div>
    );

    if (
        skillQuestsMasteryStudentData &&
        skillQuestsMasteryStudentData.reportData
    ) {
        const {
            reportData: {
                highestAccuracies,
                lowestAccuracies,
                averageAccuraciesHistory
            }
        } = skillQuestsMasteryStudentData;
        const {
            standardsInProgressLabel,
            standardsCompletedLabel,
            learningJourneysInProgressLabel,
            learningJourneysCompletedLabel,
            averageAccuracyLabel,
            highestAccuracyLabel,
            lowestAccuracyLabel,
            accuracyOverviewLabel
        } = text;
        const {
            standardsInProgressValue,
            standardsCompletedValue,
            learningJourneysInProgressValue,
            learningJourneysCompletedValue,
            averageAccuracyValue,
            highestAccuracyValue,
            lowestAccuracyValue
        } = getContentBoxValues();

        return (
            <div className={css['sq-standards-count-container']}>
                <div className={css['sq-standards-count-wrapper']}>
                    <div className={css['content-box-container']}>
                        {renderContentBox(
                            standardsInProgressLabel,
                            standardsInProgressValue,
                            null,
                            standardsCompletedLabel,
                            standardsCompletedValue,
                            null,
                            SQ_STANDARDS_COUNT_BOX_TYPES.STANDARDS
                        )}
                        {renderContentBox(
                            learningJourneysInProgressLabel,
                            learningJourneysInProgressValue,
                            null,
                            learningJourneysCompletedLabel,
                            learningJourneysCompletedValue,
                            null,
                            SQ_STANDARDS_COUNT_BOX_TYPES.SKILL_QUESTS
                        )}
                        {renderContentBox(
                            averageAccuracyLabel,
                            averageAccuracyValue,
                            null,
                            accuracyOverviewLabel,
                            getAccuracyGraphContent(averageAccuraciesHistory),
                            showAccuracyOverviewGraph
                                ? () =>
                                      getAccuracyGraphModalContent(
                                          averageAccuraciesHistory
                                      )
                                : null,
                            SQ_STANDARDS_COUNT_BOX_TYPES.AVERAGE_ACCURACY
                        )}
                        {renderContentBox(
                            highestAccuracyLabel,
                            highestAccuracyValue,
                            () =>
                                getAccuracyModalContent(
                                    highestAccuracies,
                                    highestAccuracyLabel
                                ),
                            lowestAccuracyLabel,
                            lowestAccuracyValue,
                            () =>
                                getAccuracyModalContent(
                                    lowestAccuracies,
                                    lowestAccuracyLabel
                                ),
                            SQ_STANDARDS_COUNT_BOX_TYPES.HIGHEST_ACCURACY
                        )}
                    </div>
                </div>
            </div>
        );
    }

    return null;
};

ProgressDisplay.propTypes = {
    openModal: PropTypes.func,
    skillQuestsMasteryStudentData: PropTypes.object,
    getDateLabel: PropTypes.func
};

export default styleable(css)(ProgressDisplay);



// WEBPACK FOOTER //
// ./containers/skill-quests-mastery/student/components/progress-display/progress-display.js